import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// Retraits du portefeuille Zifek.
//
// Cycle de vie :
//   1. Le commerce demande un retrait (Mobile Money, virement ou cash).
//      Le montant est IMMÉDIATEMENT bloqué sur le portefeuille.
//   2. Le SuperAdmin examine la demande et l'approuve ou la refuse.
//      Un refus rembourse automatiquement le portefeuille, avec le motif.
//
// 🔒 Sécurité : la demande, le séquestre et la décision sont exécutés par
// une fonction serveur (zifek-withdrawal) qui revérifie le solde et le rôle
// SuperAdmin. Le navigateur ne peut plus modifier une demande ni son statut.
// ─────────────────────────────────────────────────────────────

export type WithdrawalStatus = 'pending' | 'approved' | 'rejected' | 'paid';

export type WithdrawalMethod = 'mobile_money' | 'bank' | 'cash';

export interface WithdrawalRequest {
  id: number;
  userId: number;
  amount: number;
  currency: string;
  method: WithdrawalMethod;
  destination: string | null;
  note: string | null;
  status: WithdrawalStatus;
  adminNote: string | null;
  reference: string | null;
  createdAt: string;
  decidedAt: string | null;
}

export const WITHDRAWAL_MIN_AMOUNT = 10;

export const WITHDRAWAL_METHODS: { value: WithdrawalMethod; label: string; icon: string; hint: string }[] = [
  { value: 'mobile_money', label: 'Mobile Money', icon: 'ri-smartphone-line', hint: 'Numéro Mobile Money (ex. 01 23 45 67 89)' },
  { value: 'bank', label: 'Virement bancaire', icon: 'ri-bank-line', hint: 'IBAN / RIB complet' },
  { value: 'cash', label: 'Espèces en agence', icon: 'ri-hand-coin-line', hint: 'Nom de l’agence et retrait sur place' },
];

export const WITHDRAWAL_STATUS_META: Record<WithdrawalStatus, { label: string; className: string }> = {
  pending: { label: 'En attente', className: 'bg-secondary-100 text-secondary-900' },
  approved: { label: 'Approuvé', className: 'bg-accent-100 text-accent-900' },
  paid: { label: 'Payé', className: 'bg-accent-100 text-accent-900' },
  rejected: { label: 'Refusé', className: 'bg-red-50 text-red-600' },
};

function mapRow(row: Record<string, unknown>): WithdrawalRequest {
  const status = String(row.status || 'pending').toLowerCase();
  return {
    id: Number(row.id),
    userId: Number(row.user_id),
    amount: Number(row.amount) || 0,
    currency: String(row.currency || 'MAD'),
    method: (String(row.method || 'mobile_money') as WithdrawalMethod),
    destination: (row.destination as string) || null,
    note: (row.note as string) || null,
    status: (['pending', 'approved', 'rejected', 'paid'].includes(status) ? status : 'pending') as WithdrawalStatus,
    adminNote: (row.admin_note as string) || null,
    reference: (row.reference as string) || null,
    createdAt: String(row.created_at || ''),
    decidedAt: (row.decided_at as string) || null,
  };
}

/** Extrait le message d'erreur renvoyé par une fonction serveur. */
async function functionErrorMessage(error: unknown, fallback: string): Promise<string> {
  const ctx = (error as { context?: { json?: () => Promise<{ error?: string }> } })?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = await ctx.json();
      if (body?.error) return body.error;
    } catch {
      /* message par défaut */
    }
  }
  const message = (error as { message?: string })?.message;
  return message || fallback;
}

export async function requestWithdrawal(params: {
  userId: number;
  amount: number;
  currency: string;
  method: WithdrawalMethod;
  destination: string;
  note?: string;
}): Promise<{ ok: boolean; error?: string; request?: WithdrawalRequest }> {
  const { data, error } = await supabase.functions.invoke('zifek-withdrawal', {
    body: {
      action: 'request',
      userId: params.userId,
      amount: params.amount,
      currency: params.currency,
      method: params.method,
      destination: params.destination,
      note: params.note || '',
    },
  });

  if (error) {
    return { ok: false, error: await functionErrorMessage(error, 'Demande de retrait impossible.') };
  }

  const res = (data || {}) as { ok?: boolean; error?: string; withdrawal?: Record<string, unknown> };
  if (!res.ok) return { ok: false, error: res.error || 'Demande de retrait impossible.' };

  return {
    ok: true,
    request: res.withdrawal ? mapRow(res.withdrawal) : undefined,
  };
}

export async function listMyWithdrawals(userId: number, limit = 30): Promise<WithdrawalRequest[]> {
  const { data, error } = await supabase
    .from('wallet_withdrawals')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []).map((r) => mapRow(r as Record<string, unknown>));
}

export async function listAllWithdrawals(limit = 200): Promise<WithdrawalRequest[]> {
  const { data, error } = await supabase
    .from('wallet_withdrawals')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []).map((r) => mapRow(r as Record<string, unknown>));
}

/**
 * Décision du SuperAdmin — exécutée côté serveur.
 *  • approved / paid → la demande passe en traitée (le montant reste bloqué).
 *  • rejected        → le montant est automatiquement remboursé.
 * Le serveur revérifie que l'appelant est bien SuperAdmin.
 */
export async function decideWithdrawal(params: {
  id: number;
  status: Extract<WithdrawalStatus, 'approved' | 'rejected' | 'paid'>;
  adminNote: string;
  adminId?: number | null;
}): Promise<{ ok: boolean; error?: string }> {
  const adminNote = (params.adminNote || '').trim();
  if (params.status === 'rejected' && adminNote.length < 3) {
    return { ok: false, error: 'Merci d’expliquer le motif du refus au commerce.' };
  }
  if (!params.adminId) {
    return { ok: false, error: 'Administrateur non identifié.' };
  }

  const { data, error } = await supabase.functions.invoke('zifek-withdrawal', {
    body: {
      action: 'decide',
      id: params.id,
      status: params.status,
      adminNote,
      adminId: params.adminId,
    },
  });

  if (error) {
    return { ok: false, error: await functionErrorMessage(error, 'Action impossible.') };
  }

  const res = (data || {}) as { ok?: boolean; error?: string };
  if (!res.ok) return { ok: false, error: res.error || 'Action impossible.' };
  return { ok: true };
}